import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { GetDriverScheduleResponse } from './responses'

export function cacheAndCancelQuery(key: QueryKey, queryClient: QueryClient) {
  const data = queryClient.getQueriesData({ queryKey: key })
  queryClient.cancelQueries({ queryKey: key })
  return data?.[0]?.[1]
}

export function optimisticallyRemoveOrder(id: string, queryClient: QueryClient) {
  // Cancel and cache query in one step to avoid unnecessary variable assignment
  const ordersBeforeUpdate = cacheAndCancelQuery(['getAcceptedOrdersQuery'], queryClient)

  queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery'] }, (oldData: { rows: Gravitate.Order[] }) => {
    if (!oldData?.rows) return oldData // Avoid unnecessary operations

    // Use structuredClone if available for a faster deep copy (browser-native)
    const newRows = oldData.rows.filter((row) => row.id !== id)

    return structuredClone ? structuredClone({ ...oldData, rows: newRows }) : { ...oldData, rows: newRows }
  })

  return ordersBeforeUpdate
}

export async function optimisticallyAddOrder(order: Gravitate.Order, queryClient: QueryClient) {
  const ordersBeforeUpdate = cacheAndCancelQuery(['getAcceptedOrdersQuery'], queryClient)
  const orderCopy = structuredClone(order)

  queryClient.setQueriesData({ queryKey: ['getAcceptedOrdersQuery'] }, (oldData: { rows: Gravitate.Order[] }) => ({
    ...oldData,
    rows: [orderCopy, ...oldData.rows],
  }))

  return ordersBeforeUpdate
}

export async function optimisticallyAddSchedule(
  driver_schedule_id: string,
  scheduleToAdd: Gravitate.Schedule,
  queryClient: QueryClient
) {
  const schedulesBeforeUpdate = cacheAndCancelQuery(['getDriverScheduleQuery'], queryClient)

  queryClient.setQueriesData({ queryKey: ['getDriverScheduleQuery'] }, (oldData: GetDriverScheduleResponse) =>
    oldData?.map((s) => (driver_schedule_id === s.id ? scheduleToAdd : s))
  )

  return schedulesBeforeUpdate
}
